import blue from '@mui/material/colors/blue';
import green from '@mui/material/colors/green';
import orange from '@mui/material/colors/orange';
import red from '@mui/material/colors/red';
import grey from '@mui/material/colors/grey';

const NOM_MOIS = [
  'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun',
  'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc',
];

const IQG_COLORS = {
  iqg: blue[700],
  iqgLine: blue[500],
  interventions: green[300],
  sav: red[300],
  litiges: orange[300],
  mauvaisAvis: red[500],
  artisans: grey[500],
};

/**
 * Génère les options ECharts pour un graphique IQG historique.
 * @param {Array} data - Données historiques IQG
 * @param {Object} options - Options additionnelles
 * @param {boolean} options.showMauvaisAvis - Afficher les mauvais avis
 * @param {boolean} options.showNbArtisans - Afficher le nombre d'artisans
 * @returns {Object} Options ECharts
 */
export const getIQGChartOptions = (
  data,
  { showNbArtisans = false } = {}
) => {
  const labels = data.map(
    (d) => `${NOM_MOIS[d.mois - 1]} ${d.annee}`
  );

  const series = [
    {
      name: 'IQG (%)',
      type: 'line',
      yAxisIndex: 0,
      data: data.map((d) => d.iqg),
      smooth: true,
      lineStyle: { color: IQG_COLORS.iqgLine, width: 3 },
      itemStyle: { color: IQG_COLORS.iqgLine },
      connectNulls: false,
    },
    {
      name: 'Interventions',
      type: 'bar',
      yAxisIndex: 1,
      data: data.map((d) => d.nbInterventions),
      itemStyle: { color: IQG_COLORS.interventions },
    },
    {
      name: 'SAV',
      type: 'bar',
      yAxisIndex: 1,
      data: data.map((d) => d.nbSAV),
      itemStyle: { color: IQG_COLORS.sav },
    },
    {
      name: 'Litiges',
      type: 'bar',
      yAxisIndex: 1,
      data: data.map((d) => d.nbLitiges),
      itemStyle: { color: IQG_COLORS.litiges },
    },
    {
      name: 'Mauvais avis',
      type: 'bar',
      yAxisIndex: 1,
      data: data.map((d) => d.nbMauvaisAvis || 0),
      itemStyle: { color: IQG_COLORS.mauvaisAvis },
    }
  ];

  if (showNbArtisans) {
    series.push({
      name: 'Artisans actifs',
      type: 'bar',
      yAxisIndex: 1,
      data: data.map((d) => d.nbArtisans || 0),
      itemStyle: { color: IQG_COLORS.artisans },
    });
  }

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
      formatter: (params) => {
        const idx = params[0].dataIndex;
        const d = data[idx];
        const lines = [
          `<b>${labels[idx]}</b>`,
          `IQG : ${d.iqg !== null ? `${d.iqg}%` : 'N/A'}`,
          `Interventions : ${d.nbInterventions}`,
          `SAV : ${d.nbSAV}`,
          `Litiges : ${d.nbLitiges}`,
          `Mauvais avis : ${d.nbMauvaisAvis || 0}`
        ];
        if (showNbArtisans && d.nbArtisans !== undefined) {
          lines.push(`Artisans : ${d.nbArtisans}`);
        }
        return lines.join('<br/>');
      },
    },
    legend: {
      data: series.map((s) => s.name),
      bottom: 0,
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: {
        rotate: 45,
      },
    },
    yAxis: [
      {
        type: 'value',
        name: 'IQG (%)',
        min: 0,
        max: 100,
        position: 'left',
        axisLabel: {
          formatter: '{value}%',
        },
      },
      {
        type: 'value',
        name: 'Interventions',
        position: 'right',
      },
    ],
    series,
  };
};
